(function registerSection3Module(global) {
  const MOBILE_MEDIA_QUERY = global.matchMedia("(max-width: 768px)");

  const isMobileViewport = () => MOBILE_MEDIA_QUERY.matches;

  function createSection3Reveal({
    section,
    visualHandle,
    start = isMobileViewport() ? "top 78%" : "top 72%",
    end = isMobileViewport() ? "+=220px" : "+=260px",
    scrub = 1,
    contentOffsetY = isMobileViewport() ? 40 : 56,
    visualOffsetY = isMobileViewport() ? 48 : 64,
  }) {
    const content = section?.querySelector(`[handle="section-3-content"]`);
    const visual = section?.querySelector(`[handle="${visualHandle}"]`);

    if (!section || !content || !visual) {
      return;
    }

    const setInitialState = () => {
      gsap.set(content, {
        autoAlpha: 0,
        y: contentOffsetY,
      });
      gsap.set(visual, {
        autoAlpha: 0,
        y: visualOffsetY,
      });
    };

    setInitialState();

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start,
        end,
        scrub,
        invalidateOnRefresh: true,
        onRefreshInit: setInitialState,
      },
    });

    tl.to(
      content,
      {
        autoAlpha: 1,
        y: 0,
        ease: "none",
        duration: 0.28,
      },
      0,
    ).to(
      visual,
      {
        autoAlpha: 1,
        y: 0,
        ease: "none",
        duration: 0.28,
      },
      0.32,
    );
  }

  function setupSection3PcBackgroundSwap(section) {
    const SECTION_3_GIF_PLAYBACK_MS = 3000;
    const imageContainer = section?.querySelector(
      `[handle="section-3-image-container"]`,
    );
    const items = imageContainer
      ? Array.from(
          imageContainer.querySelectorAll(`[handle="section-3-image-item"]`),
        )
      : [];
    const backgroundImages = [
      "section-3-pc-image-1",
      "section-3-pc-image-2",
      "section-3-pc-image-3",
    ]
      .map((handle) => section?.querySelector(`[handle="${handle}"]`))
      .filter(Boolean);
    const gifBackgroundImages = imageContainer
      ? Array.from(
          imageContainer.querySelectorAll(
            `[handle="section-3-gif-image-item"]`,
          ),
        ).filter((gifItem) => !items.some((item) => item.contains(gifItem)))
      : [];

    if (
      !section ||
      !imageContainer ||
      !items.length ||
      !backgroundImages.length
    ) {
      return;
    }

    if (typeof imageContainer.__section3BgSwapCleanup === "function") {
      imageContainer.__section3BgSwapCleanup();
    }

    const itemImagePairs = items
      .map((item, index) => ({
        item,
        backgroundImage: backgroundImages[index],
        gifBackgroundImage:
          item.querySelector(`[handle="section-3-gif-image-item"]`) ||
          gifBackgroundImages[index] ||
          null,
      }))
      .filter(({ backgroundImage }) => backgroundImage);

    if (!itemImagePairs.length) {
      return;
    }

    let activeGifCleanup = null;
    let activeGifPair = null;
    const gifPreloadMap = new Map();

    const hideBackgroundImages = ({ animate = false } = {}) => {
      if (!animate) {
        gsap.set(backgroundImages, { autoAlpha: 0 });
        return;
      }

      gsap.to(backgroundImages, {
        autoAlpha: 0,
        duration: 0.24,
        ease: "power2.out",
        overwrite: true,
        stagger: 0,
      });
    };

    const showBackgroundImage = (targetImage) => {
      backgroundImages.forEach((backgroundImage) => {
        gsap.to(backgroundImage, {
          autoAlpha: targetImage && backgroundImage === targetImage ? 1 : 0,
          duration: 0.28,
          ease: "power2.out",
          overwrite: true,
        });
      });
    };

    const clearItemGifBackground = (item) => {
      item.style.removeProperty("--section-3-active-bg-image");
    };

    const parseCssUrl = (value) => {
      const match = value?.match(/url\((['"]?)(.*?)\1\)/);
      return match?.[2] || "";
    };

    const getUrlFromSrcset = (srcset) => {
      const firstEntry = srcset?.split(",")[0]?.trim();
      return firstEntry ? firstEntry.split(/\s+/)[0] : "";
    };

    const getGifBackgroundUrl = (gifBackgroundImage) => {
      if (!gifBackgroundImage) {
        return "";
      }

      const desktopSource = gifBackgroundImage.querySelector(
        'source[media="(min-width: 768px)"]',
      );
      const gifVisual = gifBackgroundImage.querySelector("img");
      const sourceUrl = getUrlFromSrcset(desktopSource?.srcset);
      if (sourceUrl) {
        return sourceUrl;
      }

      const imageUrl =
        gifVisual?.currentSrc ||
        gifVisual?.src ||
        gifVisual?.dataset?.src ||
        "";
      if (imageUrl) {
        return imageUrl;
      }

      const inlineVarUrl = parseCssUrl(
        gifBackgroundImage.style.getPropertyValue("--bg-image-pc"),
      );
      if (inlineVarUrl) {
        return inlineVarUrl;
      }

      const computedStyle = global.getComputedStyle(gifBackgroundImage);
      return (
        parseCssUrl(computedStyle.getPropertyValue("--bg-image-pc")) ||
        parseCssUrl(computedStyle.backgroundImage)
      );
    };

    const ensureGifPreload = (url) => {
      if (!url) {
        return null;
      }

      if (!gifPreloadMap.has(url)) {
        const preloadImage = new Image();
        preloadImage.decoding = "async";
        preloadImage.src = url;
        gifPreloadMap.set(url, preloadImage);
      }

      return gifPreloadMap.get(url);
    };

    const clearActiveGif = () => {
      activeGifCleanup?.();
      activeGifCleanup = null;
      activeGifPair = null;
    };

    const playItemGifBackground = (pair) => {
      const { item, gifBackgroundImage } = pair;
      if (!gifBackgroundImage) {
        return;
      }

      const gifUrl = getGifBackgroundUrl(gifBackgroundImage);
      if (!gifUrl) {
        return;
      }

      ensureGifPreload(gifUrl);
      clearActiveGif();
      let playbackTimerId = null;
      let isCleanedUp = false;

      const cleanup = ({ resetItem = true } = {}) => {
        if (isCleanedUp) {
          return;
        }

        isCleanedUp = true;
        if (playbackTimerId) {
          global.clearTimeout(playbackTimerId);
        }

        if (resetItem) {
          clearItemGifBackground(item);
        }
        if (activeGifCleanup === cleanup) {
          activeGifCleanup = null;
        }
        if (activeGifPair === pair) {
          activeGifPair = null;
        }
      };

      activeGifPair = pair;
      activeGifCleanup = cleanup;

      clearItemGifBackground(item);
      void item.offsetWidth;
      item.style.setProperty("--section-3-active-bg-image", `url("${gifUrl}")`);

      playbackTimerId = global.setTimeout(() => {
        if (activeGifPair === pair) {
          clearItemGifBackground(item);
        }
        cleanup({ resetItem: false });
      }, SECTION_3_GIF_PLAYBACK_MS);
    };

    itemImagePairs.forEach(({ gifBackgroundImage }) => {
      const gifUrl = getGifBackgroundUrl(gifBackgroundImage);
      if (gifUrl) {
        ensureGifPreload(gifUrl);
      }
    });

    hideBackgroundImages();

    const removeListeners = [];
    const addListener = (element, eventName, handler) => {
      element.addEventListener(eventName, handler);
      removeListeners.push(() => {
        element.removeEventListener(eventName, handler);
      });
    };

    itemImagePairs.forEach((pair) => {
      const { item, backgroundImage } = pair;
      const handleMouseEnter = () => {
        showBackgroundImage(backgroundImage);
        playItemGifBackground(pair);
      };
      const handleMouseLeave = () => {
        if (activeGifPair === pair) {
          clearActiveGif();
        }
      };

      addListener(item, "mouseenter", handleMouseEnter);
      addListener(item, "mouseleave", handleMouseLeave);
    });

    const handleContainerMouseLeave = () => {
      clearActiveGif();
      hideBackgroundImages({ animate: true });
    };

    addListener(imageContainer, "mouseleave", handleContainerMouseLeave);

    const cleanup = () => {
      clearActiveGif();
      removeListeners.splice(0).forEach((removeListener) => {
        removeListener();
      });
      backgroundImages.forEach((backgroundImage) => {
        gsap.killTweensOf(backgroundImage);
      });
      itemImagePairs.forEach(({ item }) => {
        clearItemGifBackground(item);
      });
      gsap.set(backgroundImages, {
        clearProps: "opacity,visibility",
      });
      delete imageContainer.__section3BgSwapCleanup;
    };

    imageContainer.__section3BgSwapCleanup = cleanup;

    return cleanup;
  }

  function initDesktop(section) {
    const cleanupBackgroundSwap = setupSection3PcBackgroundSwap(section);
    createSection3Reveal({
      section,
      visualHandle: "section-3-image-container",
    });

    return cleanupBackgroundSwap;
  }

  function initMobile(section) {
    createSection3Reveal({
      section,
      visualHandle: "section-3-embla",
      start: "top 82%",
      end: "+=180px",
      contentOffsetY: 32,
      visualOffsetY: 40,
    });
  }

  const refreshScrollTrigger = () => {
    if (typeof ScrollTrigger !== "undefined") {
      ScrollTrigger.refresh();
    }
  };

  const runCleanupFns = (cleanupFns = []) => {
    cleanupFns
      .filter((cleanup) => typeof cleanup === "function")
      .reverse()
      .forEach((cleanup) => {
        cleanup();
      });
  };

  let section3Context = null;
  let section3Cleanup = [];
  let section3ResizeTimerId = null;
  let section3ViewportWidth = global.innerWidth;
  let section3ViewportHeight = global.innerHeight;

  const destroy = () => {
    if (section3ResizeTimerId) {
      global.clearTimeout(section3ResizeTimerId);
      section3ResizeTimerId = null;
    }

    section3Context?.revert();
    section3Context = null;
    runCleanupFns(section3Cleanup);
    section3Cleanup = [];
  };

  const init = () => {
    const section = global.document.querySelector(`[handle="section-3"]`);

    if (!section || typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    destroy();
    section3ViewportWidth = global.innerWidth;
    section3ViewportHeight = global.innerHeight;

    section3Context = gsap.context(() => {
      if (isMobileViewport()) {
        initMobile(section);
        return;
      }

      section3Cleanup.push(initDesktop(section));
    }, section);

    global.requestAnimationFrame(refreshScrollTrigger);
  };

  const handleViewportChange = () => {
    if (
      global.innerWidth === section3ViewportWidth &&
      global.innerHeight === section3ViewportHeight
    ) {
      return;
    }

    if (section3ResizeTimerId) {
      global.clearTimeout(section3ResizeTimerId);
    }

    section3ResizeTimerId = global.setTimeout(() => {
      section3ResizeTimerId = null;

      if (
        global.innerWidth === section3ViewportWidth &&
        global.innerHeight === section3ViewportHeight
      ) {
        return;
      }

      init();
    }, 180);
  };

  const api = {
    init,
    destroy,
    initDesktop: () => {
      const section = global.document.querySelector(`[handle="section-3"]`);
      if (!section || typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
        return;
      }

      gsap.registerPlugin(ScrollTrigger);
      destroy();
      section3Context = gsap.context(() => {
        section3Cleanup.push(initDesktop(section));
      }, section);
      global.requestAnimationFrame(refreshScrollTrigger);
    },
    initMobile: () => {
      const section = global.document.querySelector(`[handle="section-3"]`);
      if (!section || typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
        return;
      }

      gsap.registerPlugin(ScrollTrigger);
      destroy();
      section3Context = gsap.context(() => {
        initMobile(section);
      }, section);
      global.requestAnimationFrame(refreshScrollTrigger);
    },
  };

  global.Section3Module = api;

  if (typeof MOBILE_MEDIA_QUERY.addEventListener === "function") {
    MOBILE_MEDIA_QUERY.addEventListener("change", init);
  } else if (typeof MOBILE_MEDIA_QUERY.addListener === "function") {
    MOBILE_MEDIA_QUERY.addListener(init);
  }

  global.addEventListener("resize", handleViewportChange);

  if (global.document.readyState === "loading") {
    global.document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  if (global.document.readyState === "complete") {
    global.requestAnimationFrame(refreshScrollTrigger);
  } else {
    global.addEventListener("load", refreshScrollTrigger, { once: true });
  }
})(window);
